import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useGetAcceleratorEnrollmentQuery } from '../graphql/api-platform-graphql';

/**
 * User-specific enrollment details for a single course within an accelerator.
 */

/**
 * Enrollment data for a single accelerator.
 */

/**
 * Map of accelerator ID to enrollment data.
 */

const GwgEnrollmentContext = /*#__PURE__*/createContext(undefined);
/**
 * Provider that fetches and manages enrollment data for multiple GWG accelerators.
 *
 * Usage:
 * ```tsx
 * <GwgEnrollmentProvider acceleratorIds={['123', '456']}>
 *   <YourComponent />
 * </GwgEnrollmentProvider>
 * ```
 */
export const GwgEnrollmentProvider = ({
  acceleratorIds,
  children
}) => {
  const [enrollmentsByCourseId, setEnrollmentsByCourseId] = useState(new Map());
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const fetchEnrollmentData = async () => {
      setIsLoading(true);
      const newEnrollmentsByCourseId = new Map();

      // Fetch enrollment data for each accelerator
      await Promise.all(acceleratorIds.map(async acceleratorId => {
        try {
          const data = await useGetAcceleratorEnrollmentQuery.fetcher({
            id: acceleratorId
          })();
          if (data.careerAccelerator) {
            var _data$careerAccelerat, _data$careerAccelerat2, _data$careerAccelerat3;
            // Extract course enrollment data
            const courseEnrollments = new Map();

            // Process curriculum sections to extract course enrollment data
            (_data$careerAccelerat = data.careerAccelerator.curriculum) === null || _data$careerAccelerat === void 0 || (_data$careerAccelerat = _data$careerAccelerat.sections) === null || _data$careerAccelerat === void 0 || _data$careerAccelerat.forEach(section => {
              section.items.forEach(item => {
                var _item$learningProduct;
                if (((_item$learningProduct = item.learningProduct) === null || _item$learningProduct === void 0 ? void 0 : _item$learningProduct.__typename) === 'Course' && item.learningProduct.id) {
                  const course = item.learningProduct;
                  if (course.userEnrollment || course.urlCourseTaking !== null) {
                    var _course$userEnrollmen, _course$userEnrollmen2, _course$userEnrollmen3, _course$userEnrollmen4, _course$userEnrollmen5, _course$userEnrollmen6, _course$urlCourseTaki;
                    courseEnrollments.set(course.id, {
                      completionPercentage: (_course$userEnrollmen = (_course$userEnrollmen2 = course.userEnrollment) === null || _course$userEnrollmen2 === void 0 ? void 0 : _course$userEnrollmen2.completionPercentage) !== null && _course$userEnrollmen !== void 0 ? _course$userEnrollmen : null,
                      completionTime: (_course$userEnrollmen3 = (_course$userEnrollmen4 = course.userEnrollment) === null || _course$userEnrollmen4 === void 0 ? void 0 : _course$userEnrollmen4.completionTime) !== null && _course$userEnrollmen3 !== void 0 ? _course$userEnrollmen3 : null,
                      lastAccessedTime: (_course$userEnrollmen5 = (_course$userEnrollmen6 = course.userEnrollment) === null || _course$userEnrollmen6 === void 0 ? void 0 : _course$userEnrollmen6.lastAccessedTime) !== null && _course$userEnrollmen5 !== void 0 ? _course$userEnrollmen5 : null,
                      urlCourseTaking: (_course$urlCourseTaki = course.urlCourseTaking) !== null && _course$urlCourseTaki !== void 0 ? _course$urlCourseTaki : null
                    });
                  }
                }
              });
            });
            newEnrollmentsByCourseId.set(acceleratorId, {
              isEnrolled: (_data$careerAccelerat2 = data.careerAccelerator.isEnrolled) !== null && _data$careerAccelerat2 !== void 0 ? _data$careerAccelerat2 : false,
              completionPercentage: (_data$careerAccelerat3 = data.careerAccelerator.completionPercentage) !== null && _data$careerAccelerat3 !== void 0 ? _data$careerAccelerat3 : undefined,
              courseEnrollments: courseEnrollments.size > 0 ? courseEnrollments : undefined
            });
          }
        } catch (error) {
          // If fetch fails, mark as not enrolled
          newEnrollmentsByCourseId.set(acceleratorId, {
            isEnrolled: false
          });
        }
      }));
      setEnrollmentsByCourseId(newEnrollmentsByCourseId);
      setIsLoading(false);
    };
    if (acceleratorIds.length > 0) {
      fetchEnrollmentData();
    } else {
      setIsLoading(false);
    }
  }, [acceleratorIds]);
  const value = useMemo(() => ({
    enrollmentsByCourseId,
    isLoading
  }), [enrollmentsByCourseId, isLoading]);
  return /*#__PURE__*/React.createElement(GwgEnrollmentContext.Provider, {
    value: value
  }, children);
};

/**
 * Hook to access enrollment data for a specific accelerator.
 *
 * @param acceleratorId - The numeric ID of the accelerator
 * @returns Enrollment data or undefined if not available
 *
 * @example
 * ```tsx
 * const enrollmentData = useGwgEnrollmentData(123);
 * if (enrollmentData?.isEnrolled) {
 *   // Show enrolled state
 * }
 * ```
 */
export const useGwgEnrollmentData = acceleratorId => {
  const context = useContext(GwgEnrollmentContext);
  if (context === undefined) {
    // If not wrapped in provider, return undefined (graceful fallback)
    return undefined;
  }
  return context.enrollmentsByCourseId.get(acceleratorId.toString());
};

/**
 * Hook to check if enrollment data is still loading.
 *
 * @returns true if enrollment data is being fetched
 */
export const useGwgEnrollmentLoading = () => {
  const context = useContext(GwgEnrollmentContext);
  if (context === undefined) {
    return false;
  }
  return context.isLoading;
};

/**
 * Hook to get an accelerator with course enrollment data merged in.
 * This merges client-side enrollment data into the static accelerator data.
 *
 * @param accelerator - The base accelerator from static props
 * @returns Accelerator with user enrollment data merged into courses
 *
 * @example
 * ```tsx
 * const acceleratorWithEnrollment = useGwgAcceleratorWithEnrollment(accelerator);
 * // Now acceleratorWithEnrollment.curriculum.sections[0].buyable.userEnrollment is populated
 * ```
 */
export const useGwgAcceleratorWithEnrollment = accelerator => {
  const enrollmentData = useGwgEnrollmentData(accelerator.numericId);
  return React.useMemo(() => {
    // If no enrollment data, return accelerator as-is
    if (!(enrollmentData !== null && enrollmentData !== void 0 && enrollmentData.courseEnrollments)) {
      return accelerator;
    }

    // Type assertion needed here since we're dynamically modifying the structure
    const curriculum = accelerator.curriculum;

    // Clone and merge course enrollment data
    return {
      ...accelerator,
      isEnrolled: enrollmentData.isEnrolled,
      completionPercentage: enrollmentData.completionPercentage,
      curriculum: {
        ...curriculum,
        sections: curriculum.sections.map(section => {
          var _enrollmentData$cours;
          return {
            ...section,
            buyable: {
              ...section.buyable,
              // Merge user enrollment data if available
              ...((_enrollmentData$cours = enrollmentData.courseEnrollments) !== null && _enrollmentData$cours !== void 0 && _enrollmentData$cours.has(section.buyable.id) ? {
                ...enrollmentData.courseEnrollments.get(section.buyable.id),
                userEnrollment: enrollmentData.courseEnrollments.get(section.buyable.id)
              } : {})
            }
          };
        })
      }
    };
  }, [accelerator, enrollmentData]);
};
//# sourceMappingURL=gwg-enrollment-context.js.map